// Folder Actions Component (Rename, Delete, Share)
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Edit2, Trash2, MoreVertical, Share2 } from 'lucide-react';
import { Folder } from '@types';
import { useFileStore } from '@stores/useFileStore';
import { useToast } from '@components/ui';
import { RenameModal } from '../file/RenameModal';
import { ShareFolderModal } from './ShareFolderModal';
import { showSuspensionToastIfNeeded } from '@utils/suspension';

interface FolderActionsProps {
  folder: Folder;
  onUpdate?: () => void;
}

export const FolderActions: React.FC<FolderActionsProps> = ({ folder, onUpdate }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { deleteFolder } = useFileStore();
  const { success, error: showError } = useToast();

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        left: rect.right - 192,
      });
    }
  }, [showMenu]);

  const handleDelete = async () => {
    try {
      await deleteFolder(folder.id);
      success('Folder deleted', `"${folder.name}" has been deleted successfully.`);
      onUpdate?.();
    } catch (err) {
      showError('Failed to delete folder', 'Please try again.');
    }
    setShowMenu(false);
    setShowDeleteConfirm(false);
  };

  const handleRename = () => {
    setShowRename(true);
    setShowMenu(false);
  };

  const handleShare = async () => {
    setShowShareModal(true);
    setShowMenu(false);

    const isSuspended = await showSuspensionToastIfNeeded(showError);
    if (isSuspended) {
      setShowShareModal(false);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}
        className="absolute top-2 right-2 p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {showMenu &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[100]"
              onClick={() => setShowMenu(false)}
            />

            {/* Menu */}
            <div
              className="fixed w-40 bg-dark-secondary/95 backdrop-blur-md border border-white/10 rounded-lg shadow-2xl z-[101] overflow-hidden"
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              <button
                onClick={handleRename}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
              >
                <Edit2 className="w-4 h-4" />
                <span className="font-inter text-sm">Rename</span>
              </button>

              <div className="h-px bg-white/10" />

              <button
                onClick={handleShare}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
              >
                <Share2 className="w-4 h-4" />
                <span className="font-inter text-sm">Share</span>
              </button>

              <div className="h-px bg-white/10" />

              <button
                onClick={() => {
                  setShowMenu(false);
                  setShowDeleteConfirm(true);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-danger hover:bg-danger/10 transition-colors text-left"
              >
                <Trash2 className="w-4 h-4" />
                <span className="font-inter text-sm">Delete</span>
              </button>
            </div>
          </>,
          document.body
        )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-dark-secondary/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 animate-fade-in">
              <h3 className="font-dela text-xl text-white mb-2">Delete Folder?</h3>
              <p className="font-inter text-white/70 mb-6">
                Are you sure you want to delete <span className="text-primary font-bold">"{folder.name}"</span>? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all font-inter font-bold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg transition-all font-inter font-bold shadow-lg shadow-red-500/20"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Rename Modal */}
      {showRename && (
        <RenameModal
          file={{ ...folder, type: 'folder' } as any}
          onClose={() => setShowRename(false)}
          onSuccess={() => {
            onUpdate?.();
            setShowRename(false);
          }}
        />
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareFolderModal
          folder={folder}
          onClose={() => {
            setShowShareModal(false);
            // Only update when modal is closed, not when share is created
            onUpdate?.();
          }}
          onSuccess={() => {
            // onSuccess is only called when share is deactivated
            onUpdate?.();
          }}
        />
      )}
    </>
  );
};

